import { useEffect } from 'react'

interface FAQItem {
    question: string
    answer: string
}

interface UseFAQSchemaOptions {
    faqs: FAQItem[]
    pageUrl?: string
}

/**
 * Hook to inject FAQ Schema markup (JSON-LD) for Google rich snippets
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */
export function useFAQSchema({ faqs, pageUrl }: UseFAQSchemaOptions) {
    useEffect(() => {
        // Remove existing FAQ schema if any
        const existingSchema = document.querySelector('script[data-schema="faq"]')
        if (existingSchema) {
            existingSchema.remove()
        }

        if (faqs.length === 0) return

        // Create FAQ Schema JSON-LD
        const faqSchema = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': faqs.map((faq) => ({
                '@type': 'Question',
                'name': faq.question,
                'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.answer
                }
            }))
        }

        // Optionally add webpage info
        if (pageUrl) {
            Object.assign(faqSchema, {
                'url': pageUrl,
                'isPartOf': {
                    '@type': 'WebSite',
                    'name': 'PlanWise',
                    'url': 'https://www.planwise.social'
                }
            })
        }

        // Create and inject script tag
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.setAttribute('data-schema', 'faq')
        script.textContent = JSON.stringify(faqSchema, null, 2)
        document.head.appendChild(script)

        // Cleanup on unmount
        return () => {
            const schemaScript = document.querySelector('script[data-schema="faq"]')
            if (schemaScript) {
                schemaScript.remove()
            }
        }
    }, [faqs, pageUrl])
}

export default useFAQSchema
